import { createContext, useContext, useState, useEffect } from 'react';
import { fetchFilters, fetchDashboardData, fetchSalesmanByDistributor } from '../api/mockApi';

const DashboardContext = createContext(null);

// Jika semua item terpilih → kirim [] ke server (berarti "no filter / all")
function toParam(selected, all) {
  return selected.length === all.length ? [] : selected;
}

export function DashboardProvider({ children }) {
  // ── Filter options (loaded from API) ────────────────────────────────────
  const [filterOptions, setFilterOptions] = useState(null);
  const [salesmanOptions, setSalesmanOptions] = useState([]);

  // ── Filter state ────────────────────────────────────────────────────────
  const [tahun,          setTahun]          = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [version,        setVersion]        = useState('v1');
  const [sector,         setSector]         = useState([]);
  const [channel,        setChannel]        = useState([]);
  const [divisi,         setDivisi]         = useState([]);
  const [salesOperation, setSalesOperation] = useState([]);
  const [typeSalesman,   setTypeSalesman]   = useState([]);
  const [salesman,       setSalesman]       = useState([]);
  const [distributor,    setDistributor]    = useState([]);
  const [produk,         setProduk]         = useState([]);

  // ── Server response state ───────────────────────────────────────────────
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Load filter options once on mount, then initialize all selections to "all"
  useEffect(() => {
    fetchFilters()
      .then(opts => {
        setFilterOptions(opts);
        setSector(opts.sector.map(o => o.key));
        setChannel(opts.channel.map(o => o.key));
        setDivisi(opts.divisi.map(o => o.key));
        setSalesOperation(opts.salesOperation.map(o => o.key));
        setTypeSalesman(opts.typeSalesman.map(o => o.key));
        setSalesmanOptions(opts.salesman);
        setSalesman(opts.salesman.map(o => o.key));
        setDistributor(opts.distributor.map(o => o.key));
        setProduk(opts.produk.map(o => o.key));
      })
      .catch(err => setError(err.message ?? 'Failed to load filters'));
  }, []);

  // Reload salesman list whenever distributor selection changes
  const distributorKey = distributor.join(',');
  useEffect(() => {
    if (!filterOptions) return;
    const selectedDist = toParam(distributor, filterOptions.distributor);
    fetchSalesmanByDistributor(selectedDist)
      .then(opts => {
        const unique = opts.filter((o, i, a) => a.findIndex(x => x.key === o.key) === i);
        setSalesmanOptions(unique);
        setSalesman(unique.map(o => o.key));
      })
      .catch(err => console.error('Failed to reload salesman list:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorKey]);

  const filterKey = [
    tahun, version,
    sector.join(','),
    channel.join(','),
    divisi.join(','),
    salesOperation.join(','),
    typeSalesman.join(','),
    salesman.join(','),
    distributor.join(','),
    produk.join(','),
  ].join('|');

  useEffect(() => {
    if (!filterOptions) return;
    setLoading(true);
    // Transformasi: semua terpilih → [] (no filter)
    const params = {
      tahun,
      version,
      sector:         toParam(sector,         filterOptions.sector),
      channel:        toParam(channel,        filterOptions.channel),
      divisi:         toParam(divisi,         filterOptions.divisi),
      salesOperation: toParam(salesOperation, filterOptions.salesOperation),
      typeSalesman:   toParam(typeSalesman,   filterOptions.typeSalesman),
      salesman:       toParam(salesman,       salesmanOptions),
      distributor:    toParam(distributor,    filterOptions.distributor),
      produk:         toParam(produk,         filterOptions.produk),
    };
    const timer = setTimeout(() => {
      fetchDashboardData(params)
        .then(res => { setData(res); setError(null); })
        .catch(err => setError(err.message ?? 'Server error'))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, filterOptions]);

  const filterState = {
    tahun, version, sector, channel, divisi,
    salesOperation, typeSalesman, salesman, distributor, produk,
  };

  const setters = {
    setTahun, setVersion, setSector, setChannel, setDivisi,
    setSalesOperation, setTypeSalesman, setSalesman, setDistributor, setProduk,
  };

  // Provide effective filterOptions with dynamic salesman list
  const effectiveFilterOptions = filterOptions
    ? { ...filterOptions, salesman: salesmanOptions }
    : null;

  return (
    <DashboardContext.Provider value={{ filterOptions: effectiveFilterOptions, filterState, setters, data, loading, error }}>
      {children}
    </DashboardContext.Provider>
  );
}

export const useDashboard = () => useContext(DashboardContext);
